"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SubFormLinkService = void 0;
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const core_shared_2 = require("@ikegps/core-shared");
const field_transform_1 = require("./field.transform");
const log_service_1 = require("../log.service");
const LINK_SUBFORM_INSTANCE = true;
class SubFormLinkService {
    constructor() {
        this.__priority = 100;
        this.log = log_service_1.Logger;
    }
    isSubFormCompatible(form) {
        return this.getRules(form) != null || form.subFormType === core_api_1.SubFormType.SPAN;
    }
    findLinks(collection, candidateCollections) {
        const subFormLinks = [];
        const location = collection.location;
        const fields = collection.schema.fields.slice();
        while (fields.length > 0) {
            const fieldSchema = fields.pop();
            if (!fieldSchema) {
                continue;
            }
            if (fieldSchema.type !== core_api_1.IkeFieldType.FORM) {
                continue;
            }
            const subform = collection.getField(fieldSchema.id);
            const rules = this.getRules(subform.schema);
            if (rules == null) {
                // FIXME: This seems wrong -- it adds instance IDs to list of field IDs, so nested subforms are never actually searched
                fields.push.apply(fields, subform.value);
                continue;
            }
            for (const candidateCollection of candidateCollections) {
                const subFormLink = this.getLink(rules, subform, location, candidateCollection);
                if (subFormLink !== null) {
                    subFormLinks.push(subFormLink);
                    continue;
                }
            }
        }
        return subFormLinks;
    }
    copySubForm(collection, subFormLinkRecord) {
        const instance = subFormLinkRecord.instance;
        const targetSubForm = collection.getField(instance.schema.id);
        const targetSubFormInstance = targetSubForm.Mutator.copyInstance(instance.parent, instance.instanceId, LINK_SUBFORM_INSTANCE);
        const rules = this.getRules(targetSubFormInstance.schema);
        targetSubFormInstance.forEachField((field) => {
            const fieldRule = rules === null || rules === void 0 ? void 0 : rules.fields[field.schema.id];
            if (!fieldRule) {
                return;
            }
            const transform = field_transform_1.FieldTransform.create(field, fieldRule);
            if (transform === null) {
                return;
            }
            transform.apply(field);
        });
        // Remove measurements which link to external captures
        const externalCaptureIds = this.getCaptureIds(subFormLinkRecord.collection, targetSubFormInstance);
        targetSubFormInstance.forEachField((field) => {
            if (!core_shared_2.CollectionModelFieldHeight.isHeightField(field) || !field.isPhotoMeasure()) {
                return;
            }
            const stateValue = field.stateValue;
            const drawableState = collection.getDrawableState(stateValue.captureId);
            const drawable = drawableState.getDrawable(stateValue.drawable);
            if (externalCaptureIds[drawable.captureId]) {
                field.Mutator.unlink();
            }
        });
        return targetSubFormInstance;
    }
    /**
     * Search for SubForm instances on the candidateCollection which pass the given rules.
     * The candidateCollection must also be within the rule's distance of the given location.
     * @param rules Defines max distance and the vector field used to search for a linkable vector end point.
     * @param subform The subForm instance which we are searching from - the "end" of the link.
     * @param location The location of the collection that the subform instance (end point) exists on.
     * @param candidateCollection The collection we are searching for a vector field on whose end point is within range of the location.
     * @returns SubFormLink describing the link.
     */
    getLink(rules, subform, location, candidateCollection) {
        var _a;
        const candidateSubForm = candidateCollection.getField(subform.schema.id);
        if (candidateSubForm == null || !candidateSubForm.hasValue()) {
            return null;
        }
        const candidateLocation = candidateCollection.location;
        const subFormLinks = [];
        for (const candidateInstance of candidateSubForm.value) {
            // Skip if the instance ID of the candidate is the same as the current subform instance.
            // NOTE: This might not be desired behavior in some situations, since instance IDs are preserved when duplicating collections.
            if (subform.getInstance(candidateInstance.instanceId) != null) {
                this.log.warn({ collection: candidateCollection.id, instance: candidateInstance.instanceId }, `Skipping SubForm link candidate because it had the same instanceId: ${candidateCollection.title} (${candidateInstance.instanceId})`);
                continue;
            }
            // Check if any rules reject any field in the candidate instance
            if (!this.matchesRules(rules, candidateInstance)) {
                this.log.info({ collection: candidateCollection.id, instance: candidateInstance.instanceId, rules }, `Skipping SubForm link candidate because it failed rules check: ${candidateCollection.title} (${candidateInstance.instanceId})`);
                continue;
            }
            const vectorField = candidateInstance.getField(rules.vector);
            const distance = this.getDistance(candidateLocation, vectorField, location);
            if (distance == null || distance >= rules.distance) {
                this.log.info({ collection: candidateCollection.id, instance: candidateInstance.instanceId, rules, distance }, `Skipping SubForm link candidate because the endpoint was too far away: ${candidateCollection.title} (${candidateInstance.instanceId})`);
                continue;
            }
            subFormLinks.push({
                collection: candidateCollection,
                instance: candidateInstance,
                distance,
            });
        }
        subFormLinks.sort(this.sortSubFormLinkRecords);
        return (_a = subFormLinks[0]) !== null && _a !== void 0 ? _a : null;
    }
    matchesRules(rules, instance) {
        return instance.forEachField((field) => {
            const fieldRule = rules.fields[field.schema.id];
            if (!fieldRule) {
                return true;
            }
            const transform = field_transform_1.FieldTransform.create(field, fieldRule);
            if (transform == null) {
                return true;
            }
            const match = transform.match(field);
            if (!match) {
                this.log.info(`Rule match failed: ${transform.constructor.name} (field=${field.schema.id}, instance=${instance.instanceId})`);
            }
            return match;
        });
    }
    getDistance(sourceLocation, sourceVectorField, targetLocation) {
        let spanLocation1;
        let spanLocation2;
        if (sourceVectorField.provider === core_shared_2.CollectionModelFieldVector.PROVIDER_P2P) {
            const captures = sourceVectorField.getCaptures();
            spanLocation1 = captures[0].target;
            spanLocation2 = captures[1].target;
        }
        else if (sourceVectorField.provider === core_shared_2.CollectionModelFieldVector.PROVIDER_MANUAL ||
            sourceVectorField.provider === core_shared_2.CollectionModelFieldVector.PROVIDER_COLLECTION) {
            spanLocation1 = core_shared_1.IkeGeodesic.projectLocation(sourceLocation, sourceVectorField.value.bearing, sourceVectorField.value.distance);
            spanLocation2 = sourceLocation != null ? sourceLocation : spanLocation1;
        }
        else {
            return null;
        }
        const distance1 = core_shared_1.IkeGeodesic.getDistance(targetLocation, spanLocation1);
        const distance2 = core_shared_1.IkeGeodesic.getDistance(targetLocation, spanLocation2);
        return Math.min(distance1, distance2);
    }
    getCaptureIds(collection, subformInstance) {
        const captureIds = {};
        collection.forEachField((field) => {
            if (core_shared_1.CaptureProvider.isCaptureProvider(field)) {
                for (const capture of field.getCaptures()) {
                    captureIds[capture.objectId] = true;
                }
            }
        });
        subformInstance.forEachField((field) => {
            if (core_shared_1.CaptureProvider.isCaptureProvider(field)) {
                for (const capture of field.getCaptures()) {
                    delete captureIds[capture.objectId];
                }
            }
        });
        return captureIds;
    }
    sortSubFormLinkRecords(record1, record2) {
        return record1.distance - record2.distance;
    }
    getRules(form) {
        var _a;
        if (form.subFormType === core_api_1.SubFormType.SPAN) {
            return new core_shared_1.SpanSubFormCopyProvider(form, (0, core_shared_1.readPoleForm)(form.getForm()));
        }
        for (const provider of (_a = form.providers) !== null && _a !== void 0 ? _a : []) {
            if (provider == null) {
                continue;
            }
            if (typeof provider === 'object') {
                if (provider.type === core_api_1.IkeProviderType.EXISTING_SPAN) {
                    return provider;
                }
            }
            else if (provider === core_api_1.IkeProviderType.EXISTING_SPAN) {
                return core_shared_2.IkeCalcExistingSpanLegacyProvider;
            }
        }
        return null;
    }
}
exports.SubFormLinkService = SubFormLinkService;
//# sourceMappingURL=subform.link.service.js.map